import React, { useCallback, useEffect, useState } from "react";
import { Container, Row, Col, Card, CardBody, Button, Alert } from "reactstrap";
import { Link } from "react-router-dom";
import { debounce } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { campaignDeleteRequest, campaignsGetRequest } from "store/campaigns/actions";
import { ApplicationState } from "store";
import CampaignFilters from "./CampaignFilters";
import CampaignsSkeleton from "./CampaignsSkeleton";
import PaginationControls from "./PaginationControls";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { Action } from "redux";
import moment from "moment";
import DeleteModal from "./DeleteModal";
import { withTranslation } from "react-i18next";
import { DropdownActions } from "Components/Common/Actions/DropdownActions";
import { toast, ToastContainer } from "react-toastify";
import Paginator from "Components/Common/Paginator";

const statusOptions = [
  { value: "", label: "All Statuses" },
  { value: "DRAFT", label: "Draft" },
  { value: "SCHEDULED", label: "Scheduled" },
  { value: "RUNNING", label: "Running" },
  { value: "COMPLETED", label: "Completed" },
  { value: "PAUSED", label: "Paused" },
  { value: "CANCELLED", label: "Cancelled" },
  { value: "DELETED", label: "Deleted" },
  { value: "FAILED", label: "Failed" },
];

const Campaigns: React.FC = (props: any) => {
  const dispatch: ThunkDispatch<any, null, Action<string>> = useDispatch();
  const { list, loading, error, pagination } = useSelector(
    (s: ApplicationState) => s.campaign
  );


  // filters
  const [status, setStatus] = useState("");
  const [searchInput, setSearchInput] = useState("");
  // Two separate date ranges
  const [createdStart, setCreatedStart] = useState<Date | null>(null);
  const [createdEnd, setCreatedEnd] = useState<Date | null>(null);
  const [scheduledStart, setScheduledStart] = useState<Date | null>(null);
  const [scheduledEnd, setScheduledEnd] = useState<Date | null>(null);
  const [isDeleteOpen, setisDeleteOpen] = useState<boolean>(false);
  const [record, setRecord] = useState<any>(null);

  // Debounced fetch
  const debouncedFetch = useCallback(
    debounce((search, page = 0, size = 20) => {
      dispatch(
        campaignsGetRequest({
          status,
          search,
          created_start: createdStart?.toISOString(),
          created_end: createdEnd?.toISOString(),
          scheduled_start: scheduledStart?.toISOString(),
          scheduled_end: scheduledEnd?.toISOString(),
          page,
          size,
          sort_by: "created_at",
          sort_order: "desc",
        })
      );
    }, 600),
    [dispatch, status, createdStart, createdEnd, scheduledStart, scheduledEnd]
  );

  // Update on status or date range change
  useEffect(() => {
    debouncedFetch(searchInput, 0, pagination?.size ?? 20);
    return debouncedFetch.cancel;
  }, [status, createdStart, createdEnd, scheduledStart, scheduledEnd]);

  // Update on search change (debounced)
  useEffect(() => {
    debouncedFetch(searchInput, 0, pagination?.size ?? 20);
    return debouncedFetch.cancel;
  }, [searchInput]);

  // Delete Campaign

  const deleteCampaign = () => {
    const handleSuccess = (body: any) => {
      setisDeleteOpen(false)
      const message = () => toast(`Campaign deleted successfully...!`, { position: "top-center", hideProgressBar: true, className: 'bg-success text-white' });
      message();

    }
    const handleError = (error: any) => {

    }
    dispatch(campaignDeleteRequest(record?.id, handleSuccess, handleError))
  }

  // For manual fetch (pagination, reset, etc.)
  const fetch = (page = 0, size = pagination?.size ?? 20) => {
    dispatch(
      campaignsGetRequest({
        status,
        search: searchInput,
        created_start: createdStart?.toISOString(),
        created_end: createdEnd?.toISOString(),
        scheduled_start: scheduledStart?.toISOString(),
        scheduled_end: scheduledEnd?.toISOString(),
        page,
        size,
        sort_by: "created_at",
        sort_order: "desc",
      })
    );
  };

  if (loading) return <CampaignsSkeleton />;
  
  return (
    <Container fluid className="py-5">
      <ToastContainer />
      {/* Header */}
      {isDeleteOpen && <DeleteModal
                      show={isDeleteOpen}
                      onCloseClick={() => setisDeleteOpen(false)}
                      props={props}
                      record={record}
                      onDelete={deleteCampaign}
                  />}
      <Row className="align-items-center mb-4">
        <Col md="6" className="d-flex align-items-center">
          <i className="ri-mail-send-line fs-2 text-primary me-2" />
          <div>
            <h2 className="mb-0">Campaigns</h2>
            <small className="text-muted">
              Track, filter, sort and paginate your multi-channel campaigns
            </small>
          </div>
        </Col>
        <Col md="6" className="text-md-end mt-3 mt-md-0">
          <Button tag={Link} to="/campaigns/new" color="primary">
            <i className="ri-add-line me-1" />
            New Campaign
          </Button>
        </Col>
      </Row>

      {/* Filters */}
      <CampaignFilters
        status={status}
        search={searchInput}
        createdStart={createdStart}
        createdEnd={createdEnd}
        scheduledStart={scheduledStart}
        scheduledEnd={scheduledEnd}
        statusOptions={statusOptions}
        onStatusChange={setStatus}
        onSearchChange={setSearchInput}
        onCreatedDateRangeChange={(s, e) => {
          setCreatedStart(s);
          setCreatedEnd(e);
        }}
        onScheduledDateRangeChange={(s, e) => {
          setScheduledStart(s);
          setScheduledEnd(e);
        }}
        onReset={() => {
          setStatus("");
          setSearchInput("");
          setCreatedStart(null);
          setCreatedEnd(null);
          setScheduledStart(null);
          setScheduledEnd(null);
        }}
      />

      {/* Error */}
      {error && (
        <Alert color="danger" className="mb-4" toggle={() => fetch()}>
          Could not load campaigns.{" "}
          <span style={{ textDecoration: "underline", cursor: "pointer" }} onClick={() => fetch()}>
            Retry
          </span>
        </Alert>
      )}

      

      {/* Table */}
      <Card className="shadow-sm mb-3">
        <CardBody className="p-0">
          {list.length === 0 ? (
            <div className="text-center p-5">
              <i className="ri-mail-unread-line fs-1 text-muted mb-3" />
              <h5 className="fw-semibold text-muted">No campaigns found</h5>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Name</th>
                    <th>Status</th>
                    <th>Scheduled</th>
                    <th>Recipients</th>
                    <th>Sent</th>
                    <th>Created At</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((c) => (
                    <tr key={c.id}>
                      
                      <td>
                        <Link to={`/campaigns/${c.id}`}>{c.name}</Link>
                      </td>
                      <td>{c.status}</td>
                      <td>
                        {c.scheduled_time_utc
                          ?  moment(c.scheduled_time_utc).format('lll')
                          : "-"}
                      </td>
                      <td>{c.total_recipients ?? "-"}</td>
                      <td>{c.sent_count ?? "-"}</td>
                      <td>{moment(c.created_at ).format('lll') ?? "_"
                      }</td>
                       <td>
                          <DropdownActions
                              row={{original: c}}
                              permissions={{}}
                              handleLeadClick={setRecord}
                              setIsDeleteOpen={setisDeleteOpen}  
                              translater={props.t}
                              module={'campaign'}
                              actionArray={['delete']}
                          />
                      </td>     
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>



      {/* Pagination */}

      <Row className="align-items-center mt-2 g-3 text-center text-sm-start">
          <div className="col-sm">
              <div className="text-muted">
                  {props.t("common.pagination.showing")}
                  <span className="fw-semibold ms-1">
                  {list.length > 0 ? `${(pagination?.offset ?? 1) + 1}-${Math.min((pagination?.offset??1) + (pagination?.size||10), pagination?.elements||10)} of ${pagination?.elements} results` : `0-0 of 0 results`}
                  </span>
              </div>
          </div>   
          <div className="col-sm-auto">
              <Paginator total={pagination && pagination.elements ? pagination.elements : 0}
                  currentPage={pagination?.page}
                  pageChanged={(which: any) => {
                      let next: any = pagination?.page;
                      if (which === "first") next = 0;
                      else if (which === "prev") next = Math.max(0, next - 1);
                      else if (which === "next") next = Math.min(pagination?.pages??1 - 1, next + 1);
                      else if (which === "last") next = pagination?.pages??1 - 1;
                      else if (typeof which === "number") next = which;
                      fetch(next-1, pagination?.size);
                    }}
                  pageSize={pagination?.size} 
                  isPageSizeChange={true}
                  onChangeInSelect={(e: any) => {
                    const newSize = Number(e.target.value);
                    fetch(0, newSize);
                  }}
                  pagination={{...pagination, size: pagination?.size}}
                  props={props}
                />
          </div>
      </Row>

      {/* {pagination && (
        <PaginationControls
          pagination={{
            page: pagination.page,
            size: pagination.size,
            elements: pagination.elements,
            first: pagination.first,
            last: pagination.last,
			pages: pagination.pages
          }}
          pageChanged={(which) => {
            let next = pagination.page;
            if (which === "first") next = 0;
            else if (which === "prev") next = Math.max(0, next - 1);
            else if (which === "next") next = Math.min(pagination.pages - 1, next + 1);
            else if (which === "last") next = pagination.pages - 1;
            else if (typeof which === "number") next = which;
            fetch(next, pagination.size);
          }}
          onChangeInSelect={(e) => {
            const newSize = Number(e.target.value);
            fetch(0, newSize);
          }}
          pageSize={pagination.size}
        />
      )} */}
    </Container>
  );
};

export default withTranslation()(Campaigns);
